import { Text } from '@/components/text';
import { Button } from '@/components/ui/button';
import { Colors } from '@/constants/theme';
import { auth } from '@/lib/firebase';
import { useAuthStore } from '@/stores/auth-store';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { sendPasswordResetEmail } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, StyleSheet, TextInput, View } from 'react-native';

export default function ForgotPasswordScreen() {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuthStore();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isLoggedIn) {
      router.replace('/');
    }
  }, [isLoggedIn, router]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: any) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await sendPasswordResetEmail(auth, data.email);
      setSuccessMessage(t('auth.resetEmailSent', 'Password reset email sent. Check your inbox.'));
    } catch (err: any) {
      console.error(err);
      setError(t('auth.resetError', 'Failed to send reset email. Please check the email address.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.container}>
      <View style={styles.card}>
        <View style={styles.header}>
          <Image
            source={require('@/assets/images/logo.png')}
            style={{ width: 200, height: 60 }}
            contentFit='contain'
            alt={t('common.logoAlt', 'PangeX Logo')}
          />
          <View style={styles.headerTextContainer}>
            <Text type='title' style={styles.title}>
              {t('auth.forgotPasswordTitle', 'Reset your password')}
            </Text>
            <Text style={styles.description}>
              {t(
                'auth.forgotPasswordDescription',
                'Enter your email address and we will send you a link to reset your password.',
              )}
            </Text>
          </View>
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>{t('auth.email', 'Email')}</Text>
          <Controller
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value } }) => (
              <TextInput
                style={styles.input}
                onBlur={onBlur}
                onChangeText={onChange}
                value={value}
                placeholder='admin@example.com'
                placeholderTextColor={Colors.light.placeholderText}
                autoCapitalize='none'
                keyboardType='email-address'
              />
            )}
            name='email'
          />
          <View style={styles.inputErrorContainer}>
            {errors.email && <Text style={styles.errorText}>{t('auth.emailRequired', 'Email is required.')}</Text>}
          </View>
        </View>

        <View style={styles.messageContainer}>
          {error && <Text style={styles.errorText}>{error}</Text>}
          {successMessage && <Text style={styles.successText}>{successMessage}</Text>}
        </View>

        <Button
          style={{ width: '100%', alignItems: 'center' }}
          onPress={handleSubmit(onSubmit)}
          label={loading ? t('auth.sending', 'Sending...') : t('auth.sendResetLink', 'Send Reset Link')}
          disabled={loading}
        />

        <View style={{ marginTop: 16, alignItems: 'center' }}>
          <Button variant='ghost' label={t('common.backToLogin', 'Back to Login')} onPress={() => router.back()} />
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    padding: 20,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 32,
    width: '100%',
    maxWidth: 450,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    gap: 5,
  },
  header: {
    alignItems: 'center',
    marginBottom: 20,
  },
  headerTextContainer: {
    width: '100%',
    marginTop: 20,
    gap: 8,
  },
  title: {
    fontSize: 18,
  },
  description: {
    fontSize: 14,
    color: Colors.light.secondaryText,
  },
  inputContainer: {
    gap: 8,
    marginBottom: 8,
  },
  label: {
    fontWeight: '500',
    color: '#333',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fafafa',
  },
  errorText: {
    color: '#FF3B30',
    fontSize: 14,
  },
  successText: {
    color: '#34C759',
    fontSize: 14,
  },
  messageContainer: {
    height: 24, // Fixed height to prevent layout shift
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 4,
  },
  inputErrorContainer: {
    height: 20, // Reserve space for one line of error text
    justifyContent: 'center',
  },
});
